import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// MUI
import { Collapse, Divider, Grid } from "@mui/material";
import { GridCellProps, GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { applyFormatter } from "@Washouts/helpers/tables";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { StringMap } from "@/Common/interfaces/types";
import {
	externalColumn,
	fundedColumn,
	getSalesPerformanceRowsWithSearchValues,
	resolveProductLineClamp,
} from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/hooks/useSalesPerformanceTableConfiguration";
import { and, nullishResolver, ternaryResolver } from "@/Common/helpers/sonar";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Hooks
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
// Types
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { FooterModelsToShow, Product, SalesPerformanceTableProps } from "@/Washouts/types/sales";
import { Issue } from "@/Common/types/tables";
// Constants
import {
	COLUMN_RESIZE_FACTOR,
	DEFAULT_COLLAPSED_SIZE,
	DEFAULT_TABLE_MAX_ROWS,
	HEADER_CLASS_NAME,
	ID_GRID_COLDEF,
} from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

const REFERENCE = "Performance";

const buildColumns = (items: Product[] = [], key = "", issues: Issue[] = []) => {
	return items.map((element: Product) => {
		const colDef: GridColDef = {
			field: `${key}_${element.product}`,
			headerName: element.product,
			type: "number",
			width: 110,
			align: "right",
			filterable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => (
				<TypographyCell
					render="absolute"
					reference={REFERENCE}
					params={params}
					issues={issues}
					hints={{ isFunded: false, isMoney: true }}
				/>
			),
			valueGetter: (_value: number, row: GridValidRowModel) => {
				const product = row?.[key]?.filter((item: Product) => item.product === element.product);
				return product?.[0]?.value || 0;
			},
			renderHeader: (params) => <TypographyHeader params={params} lineClamp={resolveProductLineClamp(element.product, 60)} />,
		};
		return colDef;
	});
};

const CUSTOMER_WIDTH = 100;
const DESCRIPTION_WIDTH = 180;

function getInitialColumns(issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		fundedColumn,
		externalColumn,
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			width: 86,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			valueGetter: (value) => {
				if (value) {
					return getFormatUTCDate(value, DATE_FORMAT.monthNameDate);
				}
				return value;
			},
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealNumber",
			headerName: "Ref. #",
			width: 75,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "customerName",
			headerName: "Customer Name",
			resizable: true,
			minWidth: CUSTOMER_WIDTH,
			maxWidth: COLUMN_RESIZE_FACTOR * CUSTOMER_WIDTH,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "salesManagerName",
			headerName: "Sales Manager",
			resizable: true,
			minWidth: CUSTOMER_WIDTH,
			maxWidth: COLUMN_RESIZE_FACTOR * CUSTOMER_WIDTH,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: 70,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "description",
			headerName: "Description",
			resizable: true,
			flex: 1,
			minWidth: DESCRIPTION_WIDTH,
			maxWidth: COLUMN_RESIZE_FACTOR * DESCRIPTION_WIDTH,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			width: 55,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "units",
			headerName: "Units",
			type: "number",
			width: 55,
			align: "right",
			headerClassName: `${HEADER_CLASS_NAME}`,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			valueFormatter: (params) => applyFormatter(params, "account"),
			renderCell: (params) => (
				<TypographyCell render="account" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

function getInitialCommissionsColumns(issues: Issue[]): GridColDef[] {
	return [
		{
			field: "frontCommission",
			headerName: "Front Commission",
			type: "number",
			width: 110,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "backCommission",
			headerName: "Back Commission",
			type: "number",
			width: 110,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "grossCommission",
			headerName: "Gross Commission",
			type: "number",
			width: 110,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
	];
}

function getAggregationToUse(aggregation = "selectedSum") {
	return aggregation;
}

function getEmptyTitle(title = "Sales Performance") {
	return title;
}

function getCollapsedStatus(collapsed = false) {
	return collapsed;
}

function SalesTable(props: Readonly<SalesPerformanceTableProps>) {
	const { data = [] } = props;
	const columnVisibility = props.columnVisibility ?? [];
	const emptyTitle = getEmptyTitle(props?.emptyTitle);

	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	const [footerModels, setFooterModels] = useState<FooterModelsToShow>(null);
	const [showColumns, setShowColumns] = useState(false);
	const [columnsToExpand, setColumnsToExpand] = useState<string[]>([]);
	const [collapsed, setCollapsed] = useState(getCollapsedStatus(props.collapsed));

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id ?? "", // _id
			columnReference: event.currentTarget?.dataset.field ?? "", // field
			tableReference: activeIssue.current?.tableReference ?? "",
			color: activeIssue.current?.color ?? "",
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const updateCurrentIssue = (color: string, currentIssues: Issue[]) => {
		let isPresent = false;
		for (const issue of currentIssues) {
			if (
				issue &&
				issue.rowReference === activeIssue?.current?.rowReference &&
				issue.columnReference === activeIssue?.current?.columnReference
			) {
				issue.color = color;
				isPresent = true;
				break;
			}
		}

		if (!isPresent) {
			currentIssues.push({
				tableReference: props?.issuesReference,
				rowReference: activeIssue?.current?.rowReference ?? "",
				columnReference: activeIssue?.current?.columnReference ?? "",
				color,
			});
		}
		return currentIssues;
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue?.rowReference === activeIssue?.current?.rowReference &&
					issue?.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	// Visibility of the columns should be controlled by state, the 'hide' prop will be deprecated in the future.
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<StringMap<boolean>>({
		_id: false,
	});

	const getCustomValue = (row: StringMap<Product[]>, field: string) => {
		const fieldName = field?.split("_");
		if (fieldName.length > 1) {
			const [rowName, productName] = fieldName;
			return row[rowName].find((item: Product) => item.product === productName)?.value ?? 0;
		}
		return 0;
	};

	const checkConditions = (row: { funded: boolean }) => {
		return row.funded || false;
	};

	const aggregations = useAggregationExtended({
		selectedSum: {
			onlySelected: props?.totalsTypeTitle?.includes("Funded") || false,
			checkConditions,
			getCustomValue,
		},
	});

	useMemo(() => {
		const columns = getInitialColumns(issues);
		const commissions = getInitialCommissionsColumns(issues);

		try {
			let frontMax = 0;
			let backMax = 0;
			let frontKey = -1;
			let backKey = -1;

			props?.data?.forEach((element, key) => {
				if (element.frontProducts?.length > frontMax) {
					frontMax = element.frontProducts.length;
					frontKey = key;
				}

				if (element.backProducts?.length > backMax) {
					backMax = element.backProducts.length;
					backKey = key;
				}
			});

			let columnVisibilityIndex = -1;

			// Front Products
			if (frontKey !== -1) {
				const frontProducts = buildColumns(props?.data?.[frontKey].frontProducts, "frontProducts", nullishResolver(issues, []));
				frontProducts.forEach((frontProduct) => {
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === frontProduct.field),
						-1
					);
					if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
						columns.push(frontProduct);
					}
				});
			}

			// Front Gross Profit
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === "frontGrossProfit"),
				-1
			);
			if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
				columns.push({
					field: "frontGrossProfit",
					headerName: "Total Front Gross",
					type: "number",
					width: 110,
					align: "right",
					headerClassName: HEADER_CLASS_NAME,
					cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
					renderCell: (params) => (
						<TypographyCell
							render="absolute"
							reference={REFERENCE}
							params={params}
							issues={issues}
							hints={{ isFunded: false }}
						/>
					),
					renderHeader: (params) => <TypographyHeader params={params} />,
				});
			}

			// Back Products
			if (backKey !== -1) {
				const backProducts = buildColumns(props?.data?.[backKey].backProducts, "backProducts", nullishResolver(issues, []));
				backProducts.forEach((backProduct) => {
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === backProduct.field),
						-1
					);
					if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
						columns.push(backProduct);
					}
				});
			}

			// Back Gross Profit
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === "backGrossProfit"),
				-1
			);
			if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
				columns.push({
					field: "backGrossProfit",
					headerName: "Total Back Gross",
					type: "number",
					width: 110,
					align: "right",
					headerClassName: HEADER_CLASS_NAME,
					cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
					renderCell: (params) => (
						<TypographyCell
							render="absolute"
							reference={REFERENCE}
							params={params}
							issues={issues}
							hints={{ isFunded: false }}
						/>
					),
					renderHeader: (params) => <TypographyHeader params={params} />,
				});
			}

			// Append commissions columns
			columns.push(...commissions);
		} catch (error) {
			errorLogger(getError(error));
		}

		setTotalColumns(columns);
	}, [data, issues]);

	const clearFilters = useCallback(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and", quickFilterLogicOperator: "and", quickFilterValues: [] },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => {
					return {
						...prevState,
						filter: { ...filter, ...cleanFilter },
					} as GridStatePremium;
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		const visibilityModel: StringMap<boolean> = {
			_id: false,
		};
		const defaultColumnsToExpand: string[] = [];
		const footerModelsToShow: FooterModelsToShow = {
			units: getAggregationToUse(props?.aggregationToUse),
			frontGrossProfit: getAggregationToUse(props?.aggregationToUse),
			backGrossProfit: getAggregationToUse(props?.aggregationToUse),
			frontCommission: getAggregationToUse(props?.aggregationToUse),
			backCommission: getAggregationToUse(props?.aggregationToUse),
			grossCommission: getAggregationToUse(props?.aggregationToUse),
		};

		const conditionalColumnPush = (args: { canPush: boolean; columnName: string }) => {
			// set the proper footer model even if the column is not visible
			footerModelsToShow[args.columnName] = getAggregationToUse(props?.aggregationToUse);
			if (args.canPush) {
				visibilityModel[args.columnName] = false;
				defaultColumnsToExpand.push(args.columnName);
			}
		};

		try {
			let frontMax = 0;
			let backMax = 0;
			let frontKey = -1;
			let backKey = -1;

			props?.data?.forEach((element, key) => {
				if (element.frontProducts?.length > frontMax) {
					frontMax = element.frontProducts.length;
					frontKey = key;
				}

				if (element.backProducts?.length > backMax) {
					backMax = element.backProducts.length;
					backKey = key;
				}
			});

			let columnName = "";
			let columnVisibilityIndex = -1;

			// Front Gross Profit
			columnName = "frontGrossProfit";
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === columnName),
				-1
			);
			conditionalColumnPush({
				canPush: and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"),
				columnName,
			});

			// Front Products
			if (frontKey !== -1) {
				props?.data?.[frontKey].frontProducts.forEach((element) => {
					columnName = `frontProducts_${element.product}`;
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === columnName),
						-1
					);
					conditionalColumnPush({
						canPush: and(
							columnVisibilityIndex !== -1,
							columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"
						),
						columnName,
					});
				});
			}

			// Back Products
			if (backKey !== -1) {
				props?.data?.[backKey].backProducts.forEach((element) => {
					columnName = `backProducts_${element.product}`;
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === columnName),
						-1
					);
					conditionalColumnPush({
						canPush: and(
							columnVisibilityIndex !== -1,
							columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"
						),
						columnName,
					});
				});
			}

			// Back Gross Profit
			columnName = "backGrossProfit";
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === columnName),
				-1
			);
			conditionalColumnPush({
				canPush: and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"),
				columnName,
			});
		} catch (error) {
			errorLogger(getError(error));
		}

		clearFilters();

		setIssues(props.issues ?? []);
		setColumnVisibilityModel(visibilityModel);
		setFooterModels(footerModelsToShow);
		setColumnsToExpand(defaultColumnsToExpand);
		setCollapsed(getCollapsedStatus(props.collapsed));

		return () => {
			setShowColumns(false);
			setFooterModels(null);
			setColumnsToExpand([]);
			setColumnVisibilityModel({
				_id: false,
			});
			setCollapsed(true);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef, clearFilters]);

	if (!totalColumns || !footerModels) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				<DataSmartTable
					maxRows={DEFAULT_TABLE_MAX_ROWS + 1}
					sx={{ width: "1400px" }}
					apiRef={apiRef}
					columns={totalColumns}
					rows={data}
					hideFooter
					disableColumnMenu
					disableRowSelectionOnClick
					disableMultipleRowSelection
					checkboxSelection={false}
					columnVisibilityModel={columnVisibilityModel}
					initialState={{
						aggregation: {
							model: footerModels,
						},
						sorting: {
							sortModel: [{ field: "saleDate", sort: "asc" }],
						},
					}}
					slots={{
						toolbar: CommonToolbar,
						noRowsOverlay: NoRowsOverlay,
						noResultsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						toolbar: {
							titleExpandible: {
								collapsed,
								hide: false,
								collapsible: true,
								title: props.title ?? REFERENCE,
								titleClasses: "page-title",
								sxContainer: { padding: "20px 32px" },
								onClick: (value: boolean) => setCollapsed(value),
							},
							filter: {
								hide: false,
							},
							search: {
								data: getSalesPerformanceRowsWithSearchValues(data),
								hide: false,
								disabled: false,
							},
							expand: {
								setColumnVisibilityModel,
								hide: false,
								expanded: showColumns,
								columnsCollapsible: columnsToExpand,
								onExpandAction: (expanded: boolean) => {
									setShowColumns(expanded);
								},
							},
						},
						noRowsOverlay: {
							title: `No ${emptyTitle} Data`,
						},
						cell: getCell(),
					}}
					customProps={{
						tableClassName: {
							"[&_>.MuiDataGrid-main]:mx-[30px] [&_>.MuiDataGrid-main]:mb-[15px]": true,
							"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px]": true,
						},
						customAggregationsFn: { ...aggregations },
					}}
					{...props.tableProps}
				/>
				{props.addIssues && (
					<AddCellIssues
						open={openAddIssues}
						anchorEl={anchorAddIssues}
						onCloseAction={() => {
							setOpenAddIssues(false);
							setAnchorAddIssues(null);
						}}
						onChangeAction={handleChangeIssues}
					/>
				)}
			</Collapse>
			<Collapse in={props.divider} collapsedSize={0} timeout={100}>
				<Divider
					className={clsx({
						"!mx-[24px] !border-b !border-[#CCC] !pt-[5px] !mt-[10px]": !props.sxSectionDivider && !collapsed,
						"!mx-[24px] !border-b !border-[#CCC] !pt-0": !props.sxSectionDivider && collapsed,
					})}
					sx={props.sxSectionDivider}
				></Divider>
			</Collapse>
		</>
	);
}

export default function SalesPerformanceTable(props: Readonly<SalesPerformanceTableProps>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider && (
					<Divider
						className={clsx({
							"!mx-[24px] !border-b !border-[#CCC]": !props.sxSectionDivider,
						})}
						sx={props.sxSectionDivider}
					></Divider>
				)}
			</>
		);
	}

	return <>{props.dataPresent && <SalesTable {...props} />}</>;
}
